import { Controller } from "@hotwired/stimulus"

// Drives the Smart Upload experience as ONE cohesive overlay:
//   intercept submit → fetch the analyze endpoint (JSON) → keep the overlay
//   up → poll the returned status_url in place while animating a step
//   checklist + progress bar → navigate ONCE to the review when ready.
// Used by both Smart PO Upload (orders/index) and Smart Inventory Updater
// (smart_updates/new) — same controller, same overlay, same flow.
export default class extends Controller {
    static targets = [
        "input", "loading", "form", "submitBtn", "submitRow",
        "textInput", "dropzone", "tabBtn", "tabPanel",
        "step", "progressBar", "error", "errorMessage"
    ]

    static values = {
        dropLabel: { type: String, default: "Drop your file to analyze" }
    }

    // The first N-1 steps advance on this cadence so the wait feels alive
    // and "smart". The LAST step holds until the poll signals ready — we
    // never claim "done" before the job actually finishes.
    static STEP_INTERVAL_MS = 2200
    static POLL_INTERVAL_MS = 2000

    connect() {
        this.hideLoading()
        this.dragCounter = 0
        // Window-level drag & drop (Slack/Gmail pattern): the whole page is
        // a drop target. Each instance manages only its OWN overlay element
        // and its OWN bound listener refs, so connect/disconnect stay
        // idempotent per instance.
        this.windowDragCounter = 0
        this.buildWindowOverlay()
        this.boundWindowDragEnter = this.windowDragEnter.bind(this)
        this.boundWindowDragOver = this.windowDragOver.bind(this)
        this.boundWindowDragLeave = this.windowDragLeave.bind(this)
        this.boundWindowDrop = this.windowDrop.bind(this)
        window.addEventListener("dragenter", this.boundWindowDragEnter)
        window.addEventListener("dragover", this.boundWindowDragOver)
        window.addEventListener("dragleave", this.boundWindowDragLeave)
        window.addEventListener("drop", this.boundWindowDrop)
        // Submit row only matters for the Paste Text flow (file uploads
        // auto-submit). Stays hidden until both: mode is "text" AND the
        // textarea has content.
        this.toggleSubmit()
    }

    disconnect() {
        this.stopStepCadence()
        this.stopPolling()
        // Turbo swaps pages without a full unload — remove every window
        // listener and this instance's overlay so nothing leaks.
        window.removeEventListener("dragenter", this.boundWindowDragEnter)
        window.removeEventListener("dragover", this.boundWindowDragOver)
        window.removeEventListener("dragleave", this.boundWindowDragLeave)
        window.removeEventListener("drop", this.boundWindowDrop)
        if (this.windowOverlay) {
            this.windowOverlay.remove()
            this.windowOverlay = null
        }
    }

    // ----- Tab switching (File / Paste Text) -----
    // Buttons and panels both carry data-mode="file|text". Clicking a tab
    // flips active state on the buttons and toggles visibility on panels.
    switchTab(event) {
        const mode = event.currentTarget.dataset.mode
        if (!mode) return

        this.tabBtnTargets.forEach((btn) => {
            const isActive = btn.dataset.mode === mode
            btn.dataset.active = isActive ? "true" : "false"
            btn.style.color = isActive ? "var(--color-primary-600)" : "var(--text-secondary)"
            btn.style.borderBottomColor = isActive ? "var(--color-primary-600)" : "transparent"
        })

        this.tabPanelTargets.forEach((panel) => {
            panel.style.display = panel.dataset.mode === mode ? "" : "none"
        })

        this.toggleSubmit()
    }

    // Reveal the submit button only when there's something to submit.
    // The File flow auto-submits in upload(), so the button is
    // exclusively a Paste Text affordance.
    toggleSubmit() {
        if (!this.hasSubmitRowTarget) return
        const mode = this.activeMode()
        const hasText = this.hasTextInputTarget && this.textInputTarget.value.trim().length > 0
        this.submitRowTarget.style.display = (mode === "text" && hasText) ? "flex" : "none"
    }

    activeMode() {
        const active = this.tabBtnTargets.find((b) => b.dataset.active === "true")
        return active ? active.dataset.mode : "file"
    }

    // File-input change handler — auto-submit on pick.
    upload(event) {
        if (this.inputTarget.files.length > 0) {
            this.submit(event)
        }
    }

    // Single entry point for BOTH the file auto-submit and the text
    // "Analyze" button (form submit). Intercepts the native submit,
    // posts the form as JSON, and keeps the overlay up the whole time.
    submit(event) {
        if (event) event.preventDefault()
        const form = this.hasFormTarget ? this.formTarget : this.element
        if (!form) return

        this.showLoading()
        this.startStepCadence()

        fetch(form.action, {
            method: "POST",
            body: new FormData(form), // carries Rails authenticity_token
            headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
            credentials: "same-origin"
        })
            .then((res) => this.handleSubmitResponse(res, form))
            .catch(() => this.fallbackSubmit(form))
    }

    async handleSubmitResponse(res, form) {
        const contentType = res.headers.get("content-type") || ""
        // No-JS path / older route / a redirect that resolved to HTML:
        // fall back to a full-page submit so the server flow still works.
        if (!contentType.includes("application/json")) {
            return this.fallbackSubmit(form)
        }

        let data
        try {
            data = await res.json()
        } catch (_e) {
            return this.fallbackSubmit(form)
        }

        // Guard rejected the upload (unsupported type / empty / too big).
        if (!res.ok || data.error) {
            this.showError(data.error || "We couldn't read that. Please try again.")
            return
        }

        if (data.status_url) {
            this.statusUrl = data.status_url
            this.startPolling(data.status_url)
        } else {
            this.fallbackSubmit(form)
        }
    }

    // Graceful degradation: do a real full-page submit (the no-JS flow:
    // POST → analyzing interstitial → po-poll → review).
    fallbackSubmit(form) {
        this.stopStepCadence()
        this.stopPolling()
        form.requestSubmit ? form.requestSubmit() : form.submit()
    }

    // Poll the status_url (same robustness as po_poll: transient errors
    // just retry next tick). When it stops "processing", finish the steps
    // and navigate once.
    startPolling(url) {
        this.stopPolling()
        this.polling = true
        this.pollTimer = setInterval(() => this.poll(url), this.constructor.POLL_INTERVAL_MS)
    }

    stopPolling() {
        this.polling = false
        if (this.pollTimer) {
            clearInterval(this.pollTimer)
            this.pollTimer = null
        }
    }

    async poll(url) {
        let state
        try {
            const res = await fetch(url, {
                headers: { Accept: "application/json" },
                credentials: "same-origin"
            })
            if (!res.ok) return // transient (auth blip, 5xx) — try again next tick
            state = (await res.json()).state
        } catch (_e) {
            return // network blip — keep polling rather than bailing out
        }
        if (!this.polling) return
        if (state && state !== "processing") {
            this.stopPolling()
            // The job is already done; status_url renders the REVIEW (or
            // bounces on error) directly, so the old interstitial is skipped.
            this.completeSteps()
            setTimeout(() => { window.location.href = url }, 450)
        }
    }

    triggerFilePicker(event) {
        // Don't fight with file picker when user clicked the hidden input itself
        if (event.target === this.inputTarget) return
        this.inputTarget.click()
    }

    // ----- Drag and drop handlers -----
    // Counter pattern below avoids flicker when dragging over child elements
    // inside the dropzone (each child fires its own enter/leave).

    dragEnter(event) {
        event.preventDefault()
        event.stopPropagation()
        this.dragCounter += 1
        this.setDropzoneActive(true)
    }

    dragOver(event) {
        // dragover MUST be prevented to allow drop
        event.preventDefault()
        event.stopPropagation()
        if (event.dataTransfer) event.dataTransfer.dropEffect = "copy"
    }

    dragLeave(event) {
        event.preventDefault()
        event.stopPropagation()
        this.dragCounter = Math.max(0, this.dragCounter - 1)
        if (this.dragCounter === 0) this.setDropzoneActive(false)
    }

    drop(event) {
        event.preventDefault()
        // stopPropagation() halts the event before it bubbles up to the
        // window-level drop listener (registered in the bubble phase on
        // `window`, the outermost node in the propagation path), so a drop
        // ON the small dropzone is never processed twice.
        event.stopPropagation()
        this.dragCounter = 0
        this.setDropzoneActive(false)

        const files = event.dataTransfer && event.dataTransfer.files
        if (!files || files.length === 0) return
        this.acceptDroppedFile(files[0], event)
    }

    // Shared by the small dropzone drop() and the window-level drop: assign
    // the dropped file to the hidden <input type="file"> so the existing
    // submit pipeline handles it identically to a click upload.
    acceptDroppedFile(file, event) {
        const dt = new DataTransfer()
        dt.items.add(file)
        this.inputTarget.files = dt.files

        // Trigger the existing upload flow (overlay + fetch submit)
        this.upload(event)
    }

    // ----- Window-level drag and drop (whole page is a drop target) -----
    // Only react to drags that actually carry files — text/link drags from
    // within the page must not trigger the overlay.
    dragCarriesFiles(event) {
        return !!(event.dataTransfer && Array.from(event.dataTransfer.types).includes("Files"))
    }

    // True while the analyzing overlay is up — a second drop mid-analysis
    // would fire a second POST and orphan the first job, so the window
    // handlers swallow drags during analysis (but still preventDefault on
    // drop, or the browser would navigate to the dropped file).
    isAnalyzing() {
        return this.hasLoadingTarget && !this.loadingTarget.classList.contains("hidden")
    }

    windowDragEnter(event) {
        if (!this.dragCarriesFiles(event)) return
        event.preventDefault()
        if (this.isAnalyzing()) return
        this.windowDragCounter += 1
        this.showWindowOverlay()
    }

    windowDragOver(event) {
        if (!this.dragCarriesFiles(event)) return
        // dragover MUST be prevented to allow drop
        event.preventDefault()
        if (event.dataTransfer) event.dataTransfer.dropEffect = "copy"
    }

    windowDragLeave(event) {
        if (!this.dragCarriesFiles(event)) return
        this.windowDragCounter = Math.max(0, this.windowDragCounter - 1)
        if (this.windowDragCounter === 0) this.hideWindowOverlay()
    }

    windowDrop(event) {
        if (!this.dragCarriesFiles(event)) return
        // ALWAYS prevent default when files are dropped, so dropping
        // anywhere on the page never navigates the browser to the PDF.
        event.preventDefault()
        this.windowDragCounter = 0
        this.hideWindowOverlay()

        // Swallow drops while a previous upload is still analyzing —
        // accepting it would double-submit and orphan the running job.
        if (this.isAnalyzing()) return

        const files = event.dataTransfer.files
        if (!files || files.length === 0) return
        this.acceptDroppedFile(files[0], event)
    }

    // Full-page drop overlay, built per instance and appended to <body>.
    // pointer-events: none is deliberate: the overlay never intercepts the
    // drag, so the drop lands on whatever is underneath and our WINDOW drop
    // listener receives it — no drop handling needed on the overlay itself.
    buildWindowOverlay() {
        const overlay = document.createElement("div")
        overlay.style.cssText =
            "display: none; position: fixed; inset: 0; z-index: 2000;" + // above the analyzing overlay (z-index: 1000)
            "align-items: center; justify-content: center;" +
            "background: rgba(15, 23, 42, 0.45); pointer-events: none;"

        const panel = document.createElement("div")
        panel.style.cssText =
            "border: 2px dashed var(--color-primary-600, #4b55c8); border-radius: 16px;" +
            "background: var(--bg-surface-elevated, #ffffff); padding: 2.5rem 3.5rem; text-align: center;"

        const headline = document.createElement("div")
        headline.textContent = this.dropLabelValue
        headline.style.cssText = "font-size: 1.125rem; font-weight: 600; color: var(--text-primary, #1e293b);"

        const subtext = document.createElement("div")
        subtext.textContent = "PDF, JPG, PNG, HEIC, Excel, Word"
        subtext.style.cssText = "font-size: 0.8125rem; color: var(--text-secondary, #64748b); margin-top: 0.375rem;"

        panel.append(headline, subtext)
        overlay.appendChild(panel)
        // Turbo snapshots <body> BEFORE disconnect() runs, so without this
        // the hidden overlay rides into the back/forward cache and a stale
        // copy is orphaned on every restore. data-turbo-temporary makes
        // Turbo strip it from snapshots; disconnect() handles the live DOM.
        overlay.setAttribute("data-turbo-temporary", "")
        document.body.appendChild(overlay)
        this.windowOverlay = overlay
    }

    showWindowOverlay() {
        if (this.windowOverlay) this.windowOverlay.style.display = "flex"
    }

    hideWindowOverlay() {
        if (this.windowOverlay) this.windowOverlay.style.display = "none"
    }

    setDropzoneActive(active) {
        if (!this.hasDropzoneTarget) return
        const el = this.dropzoneTarget
        if (active) {
            el.dataset.prevBorder = el.style.borderColor
            el.dataset.prevBg = el.style.background
            el.style.borderColor = "var(--color-primary-600, #4b55c8)"
            el.style.background = "var(--bg-hover, rgba(75, 85, 200, 0.05))"
            el.style.transform = "scale(1.01)"
        } else {
            el.style.borderColor = el.dataset.prevBorder || "var(--border-primary)"
            el.style.background = el.dataset.prevBg || "var(--bg-surface-elevated)"
            el.style.transform = ""
        }
    }

    showLoading() {
        this.loadingTarget.classList.remove("hidden")
        this.hideError()
        if (this.hasSubmitBtnTarget) {
            this.submitBtnTarget.innerHTML = 'Analyzing...'
            // Delay disabling to ensure form submission event completes
            setTimeout(() => {
                this.submitBtnTarget.disabled = true
            }, 50)
        }
        this.resetSteps()
    }

    hideLoading() {
        this.loadingTarget.classList.add("hidden")
        if (this.hasSubmitBtnTarget) {
            this.submitBtnTarget.disabled = false
            this.submitBtnTarget.innerHTML = 'Analyze'
        }
        this.stopStepCadence()
        this.stopPolling()
    }

    // ----- Step checklist + progress bar -----
    // The first steps advance on a timer; the LAST step holds until the poll
    // signals ready (completeSteps). Progress bar fills toward, but never
    // reaches, 100% until the job is actually done.
    resetSteps() {
        if (!this.hasStepTarget) return
        this.currentStep = 0
        this.stepTargets.forEach((el, i) => {
            el.dataset.state = i === 0 ? "active" : "pending"
        })
        this.setProgress(this.progressFor(0))
    }

    startStepCadence() {
        if (!this.hasStepTarget) return
        this.stopStepCadence()
        this._stepTimer = setInterval(() => {
            // Hold on the last step until the poll completes it.
            if (this.currentStep >= this.stepTargets.length - 1) {
                this.stopStepCadence()
                return
            }
            this.advanceStep()
        }, this.constructor.STEP_INTERVAL_MS)
    }

    stopStepCadence() {
        if (this._stepTimer) {
            clearInterval(this._stepTimer)
            this._stepTimer = null
        }
    }

    advanceStep() {
        if (!this.hasStepTarget) return
        const steps = this.stepTargets
        if (this.currentStep < steps.length) steps[this.currentStep].dataset.state = "done"
        this.currentStep += 1
        if (this.currentStep < steps.length) steps[this.currentStep].dataset.state = "active"
        this.setProgress(this.progressFor(this.currentStep))
    }

    // Mark every step done + fill the bar — called the instant the poll
    // reports the job is no longer processing.
    completeSteps() {
        this.stopStepCadence()
        if (this.hasStepTarget) {
            this.stepTargets.forEach((el) => { el.dataset.state = "done" })
        }
        this.setProgress(100)
    }

    // Steps before the last cap at ~85% so the bar never reads "finished"
    // while we're still waiting on the AI.
    progressFor(index) {
        if (!this.hasStepTarget) return 0
        const last = this.stepTargets.length - 1
        if (last <= 0) return 85
        return Math.min(85, 8 + Math.round((index / last) * 77))
    }

    setProgress(pct) {
        if (this.hasProgressBarTarget) this.progressBarTarget.style.width = `${pct}%`
    }

    // ----- Error handling -----
    showError(message) {
        this.stopStepCadence()
        this.stopPolling()
        if (this.hasErrorMessageTarget) this.errorMessageTarget.textContent = message
        if (this.hasErrorTarget) this.errorTarget.classList.remove("hidden")
    }

    hideError() {
        if (this.hasErrorTarget) this.errorTarget.classList.add("hidden")
    }

    // Retry from the inline error: close the overlay so the user can pick a
    // new file / edit the text and submit again.
    retry() {
        this.dismiss()
    }

    dismiss() {
        this.hideLoading()
        this.hideError()
        if (this.hasInputTarget) this.inputTarget.value = ""
    }
}
